import { fetch as apiFetch } from 'iomart-hooks';

export type PrivateFileItem = {
  id: number;
  fileKey: string;
  version?: number;
  originalName: string;
  mimeType: string;
  size: number;
  comment?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type GeneratePrivateDocumentPayload = {
  assetCode: string;
  entityType: string;
  entityId: number;
  fileKey?: string;
  fileName?: string;
  previewValues?: Record<string, any>;
  additionalValues?: Record<string, any>;
  previewLoops?: Record<string, Record<string, any>[]>;
};

export type PrivateFileGenerateButtonConfig = {
  id: string;
  label: string;
  assetCode: string;
  fileKey?: string;
  fileName?: string;
  values?: Record<string, any>;
  previewLoops?: Record<string, Record<string, any>[]>;
  additionalValues?: Array<{
    name: string;
    label: string;
    required?: boolean;
    defaultValue?: string;
    placeholder?: string;
  }>;
  createdAt?: string;
  updatedAt?: string;
};

const ENTITY_ROOT = '/api/private-files/entity';
const GENERATE_CONFIGS_ROOT = '/api/private-files/generate-configs';

export const listPrivateFiles = async (entityType: string, entityId: number): Promise<PrivateFileItem[]> => {
  const response = await apiFetch({
    url: `${ENTITY_ROOT}/${encodeURIComponent(entityType)}/${entityId}`,
    method: 'GET',
  });
  if (!response) {
    throw new Error('Failed to load private files');
  }
  return Array.isArray(response?.files) ? response.files : [];
};

export const uploadPrivateFile = async (
  entityType: string,
  entityId: number,
  fileKey: string,
  file: File
): Promise<PrivateFileItem> => {
  const formData = new FormData();
  formData.append('key', fileKey);
  formData.append('file', file, file.name);
  const response = await apiFetch({
    url: `${ENTITY_ROOT}/${encodeURIComponent(entityType)}/${entityId}`,
    method: 'POST',
    data: formData,
  });
  if (!response?.file) {
    throw new Error('Failed to upload private file');
  }
  return response?.file as PrivateFileItem;
};

export const deletePrivateFile = async (entityType: string, entityId: number, fileKey: string) => {
  const response = await apiFetch({
    url: `${ENTITY_ROOT}/${encodeURIComponent(entityType)}/${entityId}/${encodeURIComponent(fileKey)}/delete`,
    method: 'POST',
  });
  if (!response?.deleted) {
    throw new Error('Failed to delete private file');
  }
  return response;
};

export const getPrivateImageDataUrl = async (
  entityType: string,
  entityId: number,
  fileKey: string
): Promise<string> => {
  const response = await apiFetch({
    url: `${ENTITY_ROOT}/${encodeURIComponent(entityType)}/${entityId}/${encodeURIComponent(fileKey)}/data-url`,
    method: 'GET',
  });
  if (!response?.dataUrl) {
    throw new Error('Failed to load private image data');
  }
  return String(response?.dataUrl || '');
};

export const getPrivateFileBlob = async (
  entityType: string,
  entityId: number,
  fileKey: string
): Promise<Blob> => {
  const response = await apiFetch({
    url: `${ENTITY_ROOT}/${encodeURIComponent(entityType)}/${entityId}/${encodeURIComponent(fileKey)}/content`,
    method: 'GET',
    responseType: 'blob',
  });
  if (response instanceof Blob) {
    return response;
  }
  if (response?.data instanceof Blob) {
    return response.data;
  }
  throw new Error('Failed to load private file content');
};

export const getPrivateFileBlobById = async (
  entityType: string,
  entityId: number,
  fileId: number
): Promise<Blob> => {
  const response = await apiFetch({
    url: `${ENTITY_ROOT}/${encodeURIComponent(entityType)}/${entityId}/file/${fileId}/content`,
    method: 'GET',
    responseType: 'blob',
  });
  if (response instanceof Blob) {
    return response;
  }
  if (response?.data instanceof Blob) {
    return response.data;
  }
  throw new Error('Failed to load private file content');
};

export const deletePrivateFileById = async (
  entityType: string,
  entityId: number,
  fileId: number
) => {
  const response = await apiFetch({
    url: `${ENTITY_ROOT}/${encodeURIComponent(entityType)}/${entityId}/file/${fileId}/delete`,
    method: 'POST',
  });
  if (!response?.deleted) {
    throw new Error('Failed to delete private file');
  }
  return response;
};

export type PrivateFilePageInfo = {
  pageCount: number;
  pages: Array<{ width: number; height: number }>;
};

export const getPrivateFilePageInfo = async (
  entityType: string,
  entityId: number,
  fileId: number
): Promise<PrivateFilePageInfo> => {
  const response = await apiFetch({
    url: `${ENTITY_ROOT}/${encodeURIComponent(entityType)}/${entityId}/file/${fileId}/page-info`,
    method: 'GET',
  });
  if (!response?.pageCount) {
    throw new Error('Failed to load page info');
  }
  return response as PrivateFilePageInfo;
};

export type SignPrivateFilePayload = {
  imageDataUrl: string;
  pageIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
};

export const signPrivateFile = async (
  entityType: string,
  entityId: number,
  fileId: number,
  payload: SignPrivateFilePayload
): Promise<PrivateFileItem> => {
  const response = await apiFetch({
    url: `${ENTITY_ROOT}/${encodeURIComponent(entityType)}/${entityId}/file/${fileId}/sign`,
    method: 'POST',
    data: payload,
  });
  if (!response?.file) {
    throw new Error('Failed to sign private file');
  }
  return response.file as PrivateFileItem;
};

export const updatePrivateFileComment = async (
  entityType: string,
  entityId: number,
  fileId: number,
  comment: string | null
): Promise<PrivateFileItem> => {
  const response = await apiFetch({
    url: `${ENTITY_ROOT}/${encodeURIComponent(entityType)}/${entityId}/file/${fileId}/update`,
    method: 'POST',
    data: { comment: comment ?? '' },
  });
  if (!response?.file) {
    throw new Error('Failed to update file comment');
  }
  return response.file as PrivateFileItem;
};

export const generatePrivateDocument = async (
  payload: GeneratePrivateDocumentPayload
): Promise<PrivateFileItem> => {
  const response = await apiFetch({
    url: '/api/document-templates/print-to-private',
    method: 'POST',
    data: payload,
  });
  if (!response?.generated || !response?.file) {
    throw new Error('Failed to generate private document');
  }
  return response.file as PrivateFileItem;
};

export type ExtractedTemplateVariables = {
  topLevelVariables: string[];
  loopDefinitions: Array<{ loopName: string; fields: string[] }>;
  imageVariables: string[];
  variableTitles: Record<string, string>;
};

export const extractDocumentTemplateVariables = async (
  templateId: number
): Promise<ExtractedTemplateVariables> => {
  const response = await apiFetch({
    url: `/api/document-templates/${templateId}/extract-variables`,
    method: 'GET',
  });
  if (!response) {
    throw new Error('Failed to extract template variables');
  }
  return response as ExtractedTemplateVariables;
};

export type PreviewDocumentTemplateFileResult = {
  format: string;
  html?: string;
  buffer?: string;
};

export const previewDocumentTemplateFile = async (
  templateId: number,
  previewValues: Record<string, any>,
  previewLoops: Record<string, any[]>,
  options?: { preferLayout?: boolean }
): Promise<PreviewDocumentTemplateFileResult> => {
  const response = await apiFetch({
    url: `/api/document-templates/${templateId}/preview-file`,
    method: 'POST',
    data: { previewValues, previewLoops, preferLayout: options?.preferLayout ?? true },
  });
  if (!response?.html && !response?.buffer) {
    throw new Error('Failed to preview template file');
  }
  return response as PreviewDocumentTemplateFileResult;
};

/** Call preview-file?debug=1 to get diagnostics for template rendering issues */
export const debugDocumentTemplatePreview = async (
  templateId: number,
  previewValues: Record<string, any>,
  previewLoops: Record<string, any[]>
): Promise<Record<string, any>> => {
  const response = await apiFetch({
    url: `/api/document-templates/${templateId}/preview-file?debug=1`,
    method: 'POST',
    data: { previewValues, previewLoops, preferLayout: true },
  });
  return (response as any)?.debug ?? response;
};

export const getDocumentTemplateFilePageSetup = async (
  templateId: number
): Promise<{ orientation: 'portrait' | 'landscape' } | null> => {
  const response = await apiFetch({
    url: `/api/document-templates/${templateId}/file-page-setup`,
    method: 'GET',
  });
  return response as any;
};

export const listGenerateButtonConfigs = async (
  entityType: string
): Promise<PrivateFileGenerateButtonConfig[]> => {
  const response = await apiFetch({
    url: `${GENERATE_CONFIGS_ROOT}/${encodeURIComponent(entityType)}`,
    method: 'GET',
  });
  if (!response) {
    throw new Error('Failed to load generate button configs');
  }
  return Array.isArray(response?.items) ? response.items : [];
};

export const createGenerateButtonConfig = async (
  entityType: string,
  payload: Omit<PrivateFileGenerateButtonConfig, 'id' | 'createdAt' | 'updatedAt'>
): Promise<PrivateFileGenerateButtonConfig> => {
  const response = await apiFetch({
    url: `${GENERATE_CONFIGS_ROOT}/${encodeURIComponent(entityType)}`,
    method: 'POST',
    data: payload,
  });
  if (!response?.item) {
    throw new Error('Failed to create generate button config');
  }
  return response.item as PrivateFileGenerateButtonConfig;
};

export const deleteGenerateButtonConfig = async (entityType: string, id: string) => {
  const response = await apiFetch({
    url: `${GENERATE_CONFIGS_ROOT}/${encodeURIComponent(entityType)}/${encodeURIComponent(id)}/delete`,
    method: 'POST',
  });
  if (!response?.deleted) {
    throw new Error('Failed to delete generate button config');
  }
  return response;
};
